import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaSearch, FaTimes } from 'react-icons/fa';
import SEOHelmet from '../../components/SEOHelmet';
import { BreadcrumbSchema } from '../../components/SchemaMarkup';
import { mainPageSeo } from '../../data/seoData';
import { blogPosts, blogCategories } from '../../data/blogPosts';
import ProtectedImage from '../../components/ProtectedImage';
import './Blog.css';

const Blog = () => {
  const blogSeo = mainPageSeo.blog;
  const [category, setCategory] = useState('All');
  const [query, setQuery] = useState('');

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return blogPosts.filter((post) => {
      if (category !== 'All' && post.category !== category) return false;
      if (!q) return true;
      return (
        post.title.toLowerCase().includes(q) ||
        post.excerpt.toLowerCase().includes(q) ||
        post.destination.toLowerCase().includes(q) ||
        post.category.toLowerCase().includes(q)
      );
    });
  }, [category, query]);

  const isFiltered = category !== 'All' || query.trim() !== '';
  const clear = () => {
    setCategory('All');
    setQuery('');
  };

  const [lead, ...rest] = results;

  return (
    <div className="blog-page">
      <SEOHelmet
        title={blogSeo.title}
        description={blogSeo.description}
        keywords={blogSeo.keywords}
        image={blogSeo.image}
        url="https://safarnamah.in/blog"
        type="website"
      />
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://safarnamah.in' },
        { name: 'Blog', url: 'https://safarnamah.in/blog' },
      ]} />

      <section className="blog-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="eyebrow">Dispatches</span>
            <h1>Everything we wrote down</h1>
            <p>{blogPosts.length} stories from the road &mdash; what happened, what it cost us, and what we&rsquo;d do differently.</p>
          </motion.div>
        </div>
      </section>

      <div className="filter-bar">
        <div className="container filter-bar-in">
          <div className="search">
            <FaSearch aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search stories, places or topics"
              aria-label="Search the blog"
            />
          </div>

          <div className="chips" role="group" aria-label="Filter by category">
            {blogCategories.map((c) => (
              <button
                key={c}
                type="button"
                className={`chip ${category === c ? 'on' : ''}`}
                aria-pressed={category === c}
                onClick={() => setCategory(c)}
              >
                {c}
              </button>
            ))}
          </div>

          <span className="count" aria-live="polite">
            <b>{results.length}</b> {results.length === 1 ? 'story' : 'stories'}
          </span>
        </div>
      </div>

      <section className="blog-content">
        <div className="container">
          {results.length === 0 ? (
            <div className="empty">
              <h2>Nothing matches that</h2>
              <p>Try a different topic, or clear the filters to see everything.</p>
              <button type="button" className="btn btn-secondary" onClick={clear}>Clear filters</button>
            </div>
          ) : (
            <>
              {isFiltered && (
                <button type="button" className="clear-inline" onClick={clear}>
                  <FaTimes aria-hidden="true" /> Clear filters
                </button>
              )}

              {/* Lead story gets the wide treatment */}
              <motion.article
                className="lead-post"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
              >
                <Link to={`/blog/${lead.slug}`} className="lead-link">
                  <div className="lead-photo">
                    <ProtectedImage
                      src={lead.image}
                      alt={`${lead.title} - ${lead.category} in ${lead.destination} | Safarnamah Travel Blog`}
                      style={{ width: '100%', height: '100%', display: 'block' }}
                      imgStyle={{ height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  </div>
                  <div className="lead-body">
                    <span className="kicker">{lead.category} &nbsp;&middot;&nbsp; {lead.destination}</span>
                    <h2>{lead.title}</h2>
                    <p>{lead.excerpt}</p>
                    <div className="story-meta">
                      <time>{lead.date}</time>
                      <span>{lead.readTime}</span>
                    </div>
                    <span className="story-cta">Read the story &nbsp;&rarr;</span>
                  </div>
                </Link>
              </motion.article>

              <div className="blog-grid">
                {rest.map((post, index) => (
                  <motion.article
                    key={post.id}
                    className="story-card"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: Math.min(index, 5) * 0.06 }}
                    viewport={{ once: true, amount: 0.2 }}
                  >
                    <Link to={`/blog/${post.slug}`} className="story-link">
                      <div className="story-photo">
                        <ProtectedImage
                          src={post.image}
                          alt={`${post.title} - ${post.category} in ${post.destination} | Safarnamah Travel Blog`}
                          style={{ width: '100%', height: '100%', display: 'block' }}
                          imgStyle={{ height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                        <span className="story-badge">{post.category}</span>
                      </div>
                      <div className="story-body">
                        <span className="kicker">{post.destination}</span>
                        <h2>{post.title}</h2>
                        <p>{post.excerpt}</p>
                        <div className="story-meta">
                          <time>{post.date}</time>
                          <span>{post.readTime}</span>
                        </div>
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
